import Config from 'Config';
import xhr from 'xhr';
import safeStringify from 'fast-safe-stringify';
import retry from 'retry';
import { EventData } from 'lib/EventTracking';
import { MetricData } from 'lib/MetricTracking';

declare var __DEV__: string;

export const ref = { xhr }; // tests :(

export type ETLData = EventData | MetricData;

export class ETLDispatcher<ETLData> {
  buffer: ETLData[];
  sessionProperties: { [key: string]: any };
  timer: NodeJS.Timer | null;

  constructor() {
    this.buffer = [];
    this.sessionProperties = {};
    this.timer = null;

    if (typeof window !== 'undefined') {
      window.addEventListener('unload', this.flushBuffer.bind(this), false);
    }
  }

  getEndpoint(): string {
    throw new Error('Method not implemented');
  }

  requestPayload(items: ETLData[]): { [key: string]: any } {
    throw new Error('Method not implemented');
  }

  log(item: ETLData) {
    throw new Error('Method not implemented');
  }

  addSessionProperties(newSessionProperties: {}) {
    this.sessionProperties = { ...this.sessionProperties, ...newSessionProperties };
  }

  enqueue(item: ETLData) {
    if (__DEV__) {
      this.log(item);
      return;
    }

    this.buffer.push(item);

    if (this.timer === null) {
      this.timer = setTimeout(() => {
        this.flushBuffer();
        this.timer = null;
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
      }, Config.Environment.R101_EVENTS_INTERVAL);
    }
  }

  flushBuffer() {
    if (this.buffer.length === 0) {
      return;
    }

    const onError = (unsentItems: ETLData[]) => {
      this.buffer = this.buffer.concat(unsentItems);
    };

    // shallow copy so that events added to the buffer during dispatch aren't sent twice
    const bufferCopy = [...this.buffer];
    this.buffer = [];
    try {
      this.dispatchBuffer(bufferCopy, onError);
    } catch (err) {
      onError(bufferCopy);
      console.error('Failed to flushBuffer', err);
    }
  }

  dispatchBuffer(buffer: ETLData[], onError: (unsentItems: ETLData[]) => void) {
    const rawData = this.requestPayload(buffer);
    const data = safeStringify(rawData);

    const operation = retry.operation({
      retries: 5,
      factor: 2, // exponential rollback
      minTimeout: 500,
      maxTimeout: 1000 * 60,
    });

    operation.attempt(() => {
      // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
      ref.xhr(
        {
          url: Config.Environment.R101_EVENTS_URL + this.getEndpoint(),
          method: 'POST',
          useXDR: true,
          data,
        },
        function (err: any, res: any) {
          if (!res || res.statusCode >= 500) {
            const outOfRetries = !operation.retry(err);
            if (outOfRetries && err) {
              onError(buffer);
              console.warn('eventTracking failed to flush', operation.mainError(), res);
            }
          }
        }
      );
    });

    // Log bad event data, such as circular references.
    try {
      JSON.stringify(rawData);
    } catch (e) {
      console.error('Failed to serialize data: ', rawData);
    }
  }
}
